import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useRBAC } from '../../contexts/RBACContext';
import { useNavigate } from 'react-router-dom';

const ROLE_STYLES = {
  admin: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
  viewer: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-500/20',
  guest: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
};

const ROLE_ICONS = { admin: 'Shield', viewer: 'Eye', guest: 'User' };

const UserManagement = () => {
  const { isAdmin } = useRBAC();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [successMsg, setSuccessMsg] = useState('');

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (!isAdmin) { navigate('/occupancy-analytics'); return; }
    fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      if (fetchError) setError(fetchError?.message);
      else setUsers(data || []);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({ role: editRole, updated_at: new Date()?.toISOString() })
        ?.eq('id', userId);
      if (updateError) {
        setError(updateError?.message);
      } else {
        setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, role: editRole } : u));
        setEditingId(null);
        setSuccessMsg('Role updated successfully');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError('Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({ is_active: !currentStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', userId);
      if (!updateError) {
        setUsers(prev => prev?.map(u => u?.id === userId ? { ...u, is_active: !currentStatus } : u));
        setSuccessMsg(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError('Failed to update user status');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail?.trim()) {
      setInviteError('Please enter an email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(inviteEmail?.trim())) {
      setInviteError('Please enter a valid email address.');
      return;
    }
    setInviteSending(true);
    setInviteError('');
    try {
      const loginUrl = 'https://dormdash5266.builtwithrocket.new';
      const { error: fnError } = await supabase?.functions?.invoke('send-invite-email', {
        body: { email: inviteEmail?.trim(), loginUrl }
      });
      if (fnError) throw new Error(fnError?.message || 'Failed to send invitation');
      setSuccessMsg(`Invitation sent to ${inviteEmail?.trim()}`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err?.message || 'Failed to send invitation. Please try again.');
    } finally {
      setInviteSending(false);
    }
  };

  const filteredUsers = users?.filter(u => {
    const matchSearch = !search ||
      u?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      u?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchRole = filterRole === 'all' || u?.role === filterRole;
    return matchSearch && matchRole;
  });

  const roleCounts = {
    admin: users?.filter(u => u?.role === 'admin')?.length,
    viewer: users?.filter(u => u?.role === 'viewer')?.length,
    guest: users?.filter(u => u?.role === 'guest')?.length,
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              User Management
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Manage users and their roles across the system</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowInviteModal(true); setInviteEmail(''); setInviteError(''); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity"
              title="Invite User"
            >
              <Icon name="UserPlus" size={15} />
              Invite User
            </button>
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="RefreshCw" size={15} />
              Refresh
            </button>
          </div>
        </div>

        {/* Role summary cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { role: 'admin', label: 'Admins', icon: 'Shield', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10' },
            { role: 'viewer', label: 'Viewers', icon: 'Eye', color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-500/10' },
            { role: 'guest', label: 'Guests', icon: 'User', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
          ]?.map(item => (
            <div key={item?.role} className={`rounded-xl p-4 border border-border ${item?.bg}`}>
              <div className="flex items-center gap-3">
                <Icon name={item?.icon} size={20} className={item?.color} />
                <div>
                  <div className="text-2xl font-bold text-foreground">{roleCounts?.[item?.role] || 0}</div>
                  <div className="text-xs text-muted-foreground">{item?.label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Alerts */}
        {successMsg && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-sm">
            <Icon name="CheckCircle" size={15} />
            {successMsg}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-300 text-sm">
            <Icon name="AlertCircle" size={15} />
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="premium-input pl-9 w-full"
            />
          </div>
          <select
            value={filterRole}
            onChange={e => setFilterRole(e?.target?.value)}
            className="premium-input min-w-[140px]"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
            <option value="guest">Guest</option>
          </select>
        </div>

        {/* Users table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : filteredUsers?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Icon name="Users" size={32} className="mb-3 opacity-40" />
              <p className="text-sm">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">User</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Email</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Role</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Joined</th>
                    <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers?.map(u => (
                    <tr key={u?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                            {u?.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="font-medium text-foreground">{u?.full_name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u?.email}</td>
                      <td className="px-4 py-3">
                        {editingId === u?.id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editRole}
                              onChange={e => setEditRole(e?.target?.value)}
                              className="premium-input py-1 text-xs min-w-[100px]"
                            >
                              <option value="admin">Admin</option>
                              <option value="viewer">Viewer</option>
                              <option value="guest">Guest</option>
                            </select>
                            <button
                              onClick={() => handleRoleChange(u?.id)}
                              disabled={saving}
                              className="p-1.5 rounded-md bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                              {saving ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="Check" size={12} />}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_STYLES?.[u?.role] || ROLE_STYLES?.guest}`}>
                            <Icon name={ROLE_ICONS?.[u?.role] || 'User'} size={11} />
                            {u?.role?.charAt(0)?.toUpperCase() + u?.role?.slice(1)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          u?.is_active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20' :'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/20'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${u?.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          {u?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {u?.created_at ? new Date(u?.created_at)?.toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {editingId !== u?.id && (
                            <button
                              onClick={() => { setEditingId(u?.id); setEditRole(u?.role); }}
                              className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                              title="Change role"
                            >
                              <Icon name="Pencil" size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => handleToggleActive(u?.id, u?.is_active)}
                            className={`p-1.5 rounded-md border transition-colors ${
                              u?.is_active
                                ? 'border-red-200 text-red-500 hover:bg-red-50 dark:border-red-500/20 dark:hover:bg-red-500/10' :'border-emerald-200 text-emerald-500 hover:bg-emerald-50 dark:border-emerald-500/20 dark:hover:bg-emerald-500/10'
                            }`}
                            title={u?.is_active ? 'Deactivate user' : 'Activate user'}
                          >
                            <Icon name={u?.is_active ? 'UserX' : 'UserCheck'} size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Permission reference */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Info" size={15} className="text-primary" />
            Role Permission Reference
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4 font-semibold text-muted-foreground">Permission</th>
                  <th className="text-center py-2 px-4 font-semibold text-amber-600 dark:text-amber-400">Admin</th>
                  <th className="text-center py-2 px-4 font-semibold text-sky-600 dark:text-sky-400">Viewer</th>
                  <th className="text-center py-2 px-4 font-semibold text-emerald-600 dark:text-emerald-400">Guest</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  { label: 'View Dashboard & Analytics', admin: true, viewer: true, guest: false },
                  { label: 'Submit Booking Request', admin: true, viewer: false, guest: true },
                  { label: 'View All Requests', admin: true, viewer: true, guest: false },
                  { label: 'Approve / Reject Requests', admin: true, viewer: false, guest: false },
                  { label: 'Create / Edit / Delete Data', admin: true, viewer: false, guest: false },
                  { label: 'Manage Check-in / Check-out', admin: true, viewer: false, guest: false },
                  { label: 'Manage Rooms', admin: true, viewer: false, guest: false },
                  { label: 'Manage Users & Roles', admin: true, viewer: false, guest: false },
                ]?.map((row, i) => (
                  <tr key={i} className="hover:bg-muted/20">
                    <td className="py-2 pr-4 text-foreground">{row?.label}</td>
                    {['admin', 'viewer', 'guest']?.map(r => (
                      <td key={r} className="py-2 px-4 text-center">
                        {row?.[r]
                          ? <Icon name="CheckCircle" size={14} className="inline text-emerald-500" />
                          : <Icon name="XCircle" size={14} className="inline text-red-400 opacity-60" />
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="UserPlus" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Invite User</h2>
                    <p className="text-xs text-muted-foreground">Send an invitation email with the login link</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={15} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Icon name="Mail" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      placeholder="Enter email address..."
                      value={inviteEmail}
                      onChange={e => { setInviteEmail(e?.target?.value); setInviteError(''); }}
                      onKeyDown={e => e?.key === 'Enter' && handleSendInvite()}
                      className="premium-input pl-9 w-full"
                      autoFocus
                    />
                  </div>
                  {inviteError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      {inviteError}
                    </p>
                  )}
                </div>

                <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 flex items-start gap-2">
                  <Icon name="Info" size={13} className="mt-0.5 flex-shrink-0 text-primary" />
                  The invited user will receive an email with a direct link to the DormDash login page.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvite}
                  disabled={inviteSending || !inviteEmail?.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviteSending ? (
                    <><Icon name="Loader2" size={14} className="animate-spin" /> Sending...</>
                  ) : (
                    <><Icon name="Send" size={14} /> Send Invitation</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default UserManagement;
